<template>
    <button
        type="button"
        class="core-icon-button"
        :class="classes"
        :disabled="disabled"
        :aria-label="tooltip ? ($te(tooltip) ? $t(tooltip) : tooltip) : undefined"
        v-tooltip="tooltip ? { value: $te(tooltip) ? $t(tooltip) : tooltip } : null"
        @mouseover="handleMouseOver"
        @mouseleave="handleMouseLeave"
        @click="handleClick"
    >
        <slot v-if="$slots.default" />
        <CoreIcon
            v-else-if="icon"
            :name="resolvedIconName"
            :size="iconSize"
            :fillColor="iconColor"
        />
    </button>
</template>

<script>
import CoreIcon from '@/components/core/CoreIcon.vue'

export default {
    name: 'CoreIconButton',
    components: { CoreIcon },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },

        color: {
            type: String,
            default: 'primary',
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        tooltip: {
            type: String,
            default: null,
        },

        icon: {
            type: String,
            default: null,
        },

        iconHover: {
            type: String,
            default: null,
        },

        iconActive: {
            type: String,
            default: null,
        },

        iconDisabled: {
            type: String,
            default: null,
        },

        iconSize: {
            type: [Number, String],
            default: 22,
        },

        iconColor: {
            type: String,
            default: 'currentColor',
        },
    },

    data() {
        return {
            shouldAnimate: false,
            isHovered: false,
            animationTimer: null,
        }
    },

    computed: {
        resolvedIconName() {
            if (this.disabled && this.iconDisabled) {
                return this.iconDisabled
            }
            if (this.isHovered && this.iconHover) {
                return this.iconHover
            }
            if (this.isActive && this.iconActive) {
                return this.iconActive
            }
            return this.icon
        },

        classes() {
            return {
                'core-icon-button--active': this.isActive,
                'core-icon-button--color-primary': this.color === 'primary',
                'core-icon-button--color-primary-light': this.color === 'primary-light',
                'core-icon-button--color-secondary': this.color === 'secondary',
                'core-icon-button--color-warning': this.color === 'warning',
                'core-icon-button--color-warning-light': this.color === 'warning-light',
                'core-icon-button--color-error': this.color === 'error',
                'core-icon-button--color-error-light': this.color === 'error-light',
                'core-icon-button--disabled': this.disabled,
                'core-icon-button--pop': this.shouldAnimate,
            }
        },
    },

    watch: {
        disabled(newVal, oldVal) {
            if (oldVal === true && newVal === false) {
                this.shouldAnimate = true
                clearTimeout(this.animationTimer)
                this.animationTimer = setTimeout(() => {
                    this.shouldAnimate = false
                }, 400)
            }
        },
    },

    beforeUnmount() {
        clearTimeout(this.animationTimer)
    },

    methods: {
        handleMouseOver() {
            this.isHovered = true
            this.$emit('mouseover')
        },

        handleMouseLeave() {
            this.isHovered = false
            this.$emit('mouseleave')
        },

        handleClick(event) {
            if (this.disabled) {
                event.preventDefault()
                event.stopPropagation()
                return
            }
            event.stopPropagation()
            this.$emit('buttonClick', event)
        },
    },
}
</script>

<style scoped>
.core-icon-button {
    width: 40px;
    height: 40px;
    font-size: 1.4rem;
    font-weight: bold;
    cursor: pointer;
    background-color: transparent;

    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 5px;
    border-radius: 8px;

    transition: 0.1s all;
}

.core-icon-button--color-primary {
    color: var(--text-dark);
    border: 1px solid var(--color-primary);
    background-color: var(--color-primary);

    &:hover {
        background-color: var(--color-primary-600);
        border: 1px solid var(--color-primary-600);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-primary {
    background-color: var(--color-primary-600);
    color: var(--text-dark);
    border: 1px solid var(--color-primary-600);

    &:hover {
        background-color: var(--color-primary-700);
        border: 1px solid var(--color-primary-700);
    }
}

.core-icon-button--color-primary-light {
    color: var(--text-dark);
    border: 1px solid var(--color-primary-300);
    background-color: var(--color-primary-200);

    &:hover {
        background-color: var(--color-primary-500);
        border: 1px solid var(--color-primary-500);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-primary-light {
    background-color: var(--color-primary-500);
    color: var(--text-dark);
    border: 1px solid var(--color-primary-500);

    &:hover {
        background-color: var(--color-primary-600);
        border: 1px solid var(--color-primary-600);
    }
}

.core-icon-button--color-secondary {
    color: var(--text-dark);
    border: 1px solid var(--color-secondary-150);
    background-color: var(--color-secondary-150);

    &:hover {
        background-color: var(--color-secondary-200);
        border: 1px solid var(--color-secondary-200);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-secondary {
    background-color: var(--color-secondary-600);
    color: var(--text-dark);
    border: 1px solid var(--color-secondary-600);

    &:hover {
        background-color: var(--color-secondary-500);
        border: 1px solid var(--color-secondary-500);
    }
}

.core-icon-button--color-warning {
    color: var(--text-dark);
    border: 1px solid var(--color-warning);
    background-color: var(--color-warning);

    &:hover {
        background-color: var(--color-yellow-300);
        border: 1px solid var(--color-yellow-300);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-warning {
    background-color: var(--color-warning-500);
    color: var(--text-dark);
    border: 1px solid var(--color-warning-500);

    &:hover {
        background-color: var(--color-yellow-300);
        border: 1px solid var(--color-yellow-300);
    }
}

.core-icon-button--color-warning-light {
    color: var(--text-dark);
    border: 1px solid var(--color-yellow-300);
    background-color: var(--color-yellow-200);

    &:hover {
        background-color: var(--color-yellow-400);
        border: 1px solid var(--color-yellow-500);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-warning-light {
    background-color: var(--color-yellow-500);
    color: var(--text-dark);
    border: 1px solid var(--color-yellow-500);

    &:hover {
        background-color: var(--color-yellow-400);
        border: 1px solid var(--color-yellow-500);
    }
}

.core-icon-button--color-error {
    color: var(--text-dark);
    border: 1px solid var(--color-red-700);
    background-color: var(--color-red-700);

    &:hover {
        background-color: var(--color-red-500);
        border: 1px solid var(--color-red-500);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-error {
    background-color: var(--color-red-500);
    color: var(--text-dark);
    border: 1px solid var(--color-red-500);

    &:hover {
        background-color: var(--color-red-400);
        border: 1px solid var(--color-red-400);
    }
}

.core-icon-button--color-error-light {
    color: var(--text-dark);
    border: 1px solid var(--color-red-300);
    background-color: var(--color-red-300);

    &:hover {
        background-color: var(--color-red-500);
        border: 1px solid var(--color-red-500);
        color: var(--text-dark);
    }
}

.core-icon-button--active.core-icon-button--color-error-light {
    background-color: var(--color-red-500);
    color: var(--text-dark);
    border: 1px solid var(--color-red-500);

    &:hover {
        background-color: var(--color-red-500);
        border: 1px solid var(--color-red-500);
    }
}

.core-icon-button--disabled {
    color: var(--color-neutral);
    border: 1px solid var(--color-neutral);
    background-color: transparent;

    &:hover {
        cursor: not-allowed;
        background-color: transparent;
        border: 1px solid var(--color-neutral);
        color: var(--color-neutral);
    }
}

.core-icon-button--pop {
    animation: popAnimation 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes popAnimation {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
}
</style>
